import { Dispatch, SetStateAction } from 'react';

import { postOnboarding } from '#/api/account';

import { prepareErrorMessage } from '#/features/wallet/ethereum';
import {
  ParadexEmbeddedConfigurable,
  StarknetChainConfigurable,
  WalletState,
} from '#/features/wallet/wallet-context';
import StarknetWallet from '#/features/starknet-wallet/wallet';
import { buildOnboardingTypedData } from '#/features/wallet/chain-l2';
import EthereumWallet from '#/features/ethereum-wallet/wallet';
import { getIsEthereumWalletCompliantStorageKey } from '#/features/storage/storage';
import { logEvent } from '#/features/logging/logging';

import { AsyncResult } from '#/utils/types';
import { setStorageItem } from '#/utils/localStorage';

export default function actionPerformOnboarding(
  ...params: Parameters<typeof performOnboarding>
) {
  return async () => performOnboarding(...params);
}

async function performOnboarding(
  walletState: WalletState,
  setState: Dispatch<SetStateAction<WalletState>>,
  options: StarknetChainConfigurable & ParadexEmbeddedConfigurable,
): AsyncResult<void> {
  if (walletState.isOnboarded) {
    return { ok: true, data: undefined };
  }

  const actions = prepareActions(setState);

  actions.resetError();
  actions.setStatus('signing_onboarding');

  try {
    const onboardingTypedData = buildOnboardingTypedData(
      options.starknetChainId,
    );
    const signature = StarknetWallet.signTypedData(onboardingTypedData);
    const starknetPublicKey = `0x${StarknetWallet.getPublicKey()}`;
    const starknetAddress = StarknetWallet.getAddress();
    const ethereumAddressRecovered = StarknetWallet.getEthereumAddress();

    const ethereumAddressConnected = EthereumWallet.getAddress();

    if (ethereumAddressConnected !== ethereumAddressRecovered) {
      logEvent(
        `Failed to perform onboarding. Currently active Ethereum address does not match the address that generated the Starknet address.`,
        {
          ethereumAddressConnected,
          ethereumAddressRecovered,
          starknetAddress,
        },
      );
      // WIP: to be translated
      actions.setError(
        `A different Ethereum address (${ethereumAddressRecovered}) is still associated with this session. ` +
          `To connect with (${ethereumAddressConnected}) first click "Disconnect Wallet" and try again.`,
      );
      actions.setStatus('wallet_connected_idle');
      return { ok: false, error: null };
    }

    const resp = await postOnboarding({
      signature,
      starknetPublicKey,
      starknetAddress,
      ethereumAddress: ethereumAddressConnected,
      parentUserToken: options.parentUserToken,
    });

    if (!resp.ok) {
      const error = [400, 401, 403].includes(resp.status)
        ? null
        : new Error(`Error onboarding: ${resp.message}`, {
            cause: resp.error,
          });
      actions.setError(resp.message);
      actions.setStatus('wallet_connected_idle');
      return { ok: false, error };
    }

    // Only after successful onboarding, we can remember that the wallet is compliant
    setStorageItem(
      getIsEthereumWalletCompliantStorageKey(ethereumAddressConnected),
      true,
    );
  } catch (err) {
    const description = 'Failed to perform onboarding';
    const { message, isException } = prepareErrorMessage(description, err);
    actions.setError(message);
    actions.setStatus('wallet_connected_idle');
    if (isException) {
      const error = new Error(description, { cause: err });
      return { ok: false, error };
    }
    return { ok: false, error: null };
  }

  actions.setIsOnboarded(true);
  actions.setStatus('wallet_connected_idle');
  return { ok: true, data: undefined };
}

function prepareActions(setState: Dispatch<SetStateAction<WalletState>>) {
  function setStatus(status: WalletState['step']) {
    setState((state) => ({ ...state, step: status }));
  }

  function setIsOnboarded(isOnboarded: WalletState['isOnboarded']) {
    setState((state) => ({ ...state, isOnboarded }));
  }

  const setError = (error: string) => {
    setState((state) => ({ ...state, error }));
  };

  const resetError = () => {
    setState((state) => ({ ...state, error: '' }));
  };

  return { setStatus, setIsOnboarded, setError, resetError };
}
